import { AxiosError } from 'axios';
import axios from 'utils/axios.config';
import { types, flow, Instance } from 'mobx-state-tree';
import { withAlertStore } from 'components/Core/CustomAlert/store';
import { EntityType, ErrorData, IResponse, StatusType } from 'types/common';
import {
  IBrandRequest,
  IModerateEntityRequest,
  IPointRequest,
  IProductRequest,
  IRequestsCounts,
  IEntityRequest,
  IBrandRequestData,
  IPointRequestData,
  IProductRequestData,
  IAfterManageRequestData,
  ICatalogueRequest,
  ICatalogueRequestData,
} from './types';
import { catalogueRequestPagesMock, catalogueRequestsMock } from './mock';

const { model, frozen, maybeNull, number } = types;

export const RequestsStore = model('RequestsStore', {
  requestsBrandsCount: number,
  requestsPointsCount: number,
  requestsProductsCount: number,
  requestsCataloguesCount: number,
  brandRequests: maybeNull(frozen<IBrandRequest[]>()),
  pointRequests: maybeNull(frozen<IPointRequest[]>()),
  productRequests: maybeNull(frozen<IProductRequest[]>()),
  catalogueRequests: maybeNull(frozen<ICatalogueRequest[]>()),
  brandRequestData: maybeNull(frozen<IBrandRequestData>()),
  pointRequestData: maybeNull(frozen<IPointRequestData>()),
  productRequestData: maybeNull(frozen<IProductRequestData>()),
  catalogueRequestData: maybeNull(frozen<ICatalogueRequestData>()),
})
  .extend(withAlertStore)
  .actions((self) => ({
    setRequestsBrandsCount(count: number) {
      self.requestsBrandsCount = count;
    },
    setRequestsPointsCount(count: number) {
      self.requestsPointsCount = count;
    },
    setRequestsProductsCount(count: number) {
      self.requestsProductsCount = count;
    },
    setRequestsCataloguesCount(count: number) {
      self.requestsCataloguesCount = count;
    },
    setBrandRequests(data: IBrandRequest[] | null) {
      self.brandRequests = data;
    },
    setPointRequests(data: IPointRequest[] | null) {
      self.pointRequests = data;
    },
    setProductRequests(data: IProductRequest[] | null) {
      self.productRequests = data;
    },
    setCatalogueRequests(data: ICatalogueRequest[] | null) {
      self.catalogueRequests = data;
    },
    setBrandRequestData(data: IBrandRequestData | null) {
      self.brandRequestData = data;
    },
    setPointRequestData(data: IPointRequestData | null) {
      // add isFixed flag to categories to change their behavior and visualization (prohibit deletion)
      self.pointRequestData = data
        ? { ...data, categories: data.categories.map((item) => ({ ...item, isFixed: true })) }
        : data;
    },
    setProductRequestData(data: IProductRequestData | null) {
      self.productRequestData = data;
    },
    setCatalogueRequestData(data: ICatalogueRequestData | null) {
      self.catalogueRequestData = data;
    },
    updateDataAfterManageRequest<T extends { id: string }>(data: T[], status: StatusType | null, id: string) {
      return status
        ? data.map((item) => (item.id === id ? { ...item, status } : item))
        : data.filter((item) => item.id !== id);
    },
  }))
  // GENERAL HANDLING
  .actions((self) => ({
    errorHandling({ response }: AxiosError<IResponse<ErrorData>>) {
      const msg = response ? response?.data.message || 'Internal server error' : 'Application error';
      self.alertStore.showAlert(msg);
      throw new Error(msg);
    },
  }))
  // REQUESTS API
  .actions((self) => ({
    getRequestsCount: flow(function* () {
      yield axios
        .get<IResponse<IRequestsCounts>>(`request/requests-count`)
        .then(
          ({
            data: {
              data: { requestsBrandsCount, requestsPointsCount, requestsProductsCount, requestsCataloguesCount },
            },
          }) => {
            self.setRequestsBrandsCount(requestsBrandsCount);
            self.setRequestsPointsCount(requestsPointsCount);
            self.setRequestsProductsCount(requestsProductsCount);
            self.setRequestsCataloguesCount(requestsCataloguesCount || catalogueRequestsMock.length);
          }
        )
        .catch(self.errorHandling);
    }),
    getRequestsData: flow(function* (entity: EntityType) {
      if (entity === 'catalogues') {
        self.setCatalogueRequests(catalogueRequestsMock);
        return;
      }
      yield axios
        .get(`request/${entity}-requests`)
        .then(({ data: { data } }) => {
          switch (entity) {
            case 'brands':
              self.setBrandRequests(data);
              break;
            case 'points':
              self.setPointRequests(data);
              break;
            case 'products':
              self.setProductRequests(data);
              break;
            // case 'catalogues':
            //   self.setCatalogueRequests(data);
            //   break;
          }
        })
        .catch(self.errorHandling);
    }),
    getRequestsEntityData: flow(function* ({ id, entity }: IEntityRequest) {
      if (entity === 'catalogues') {
        const catalogueRequest = catalogueRequestsMock.find((item) => item.id === id);
        self.setCatalogueRequestData(
          catalogueRequest
            ? {
                ...catalogueRequest,
                changedFieldsNames: ['pages'],
                pages: catalogueRequestPagesMock,
                changedPagesPositions: [1, 2],
              }
            : null
        );
        return;
      }
      yield axios
        .get(`request/${entity}-request/${id}`)
        .then(({ data: { data } }) => {
          switch (entity) {
            case 'brands':
              self.setBrandRequestData(data);
              break;
            case 'points':
              self.setPointRequestData(data);
              break;
            case 'products':
              self.setProductRequestData(data);
              break;
            // case 'catalogues':
            //   self.setCatalogueRequestData(data);
            //   break;
          }
        })
        .catch(self.errorHandling);
    }),
    manageRequest: flow(function* ({ id, entity, status, adminMessage }: IModerateEntityRequest) {
      const url = status ? 'decline-revise' : 'approve';
      const body = status && {
        status,
        adminMessage,
      };
      yield axios
        .patch<IResponse<IAfterManageRequestData>>(`request/${entity}-${url}/${id}`, body)
        .then(
          ({
            data: {
              data: { count },
            },
          }) => {
            switch (entity) {
              case 'brands':
                self.setRequestsBrandsCount(count);
                self.brandRequests &&
                  self.setBrandRequests(self.updateDataAfterManageRequest(self.brandRequests, status, id));
                break;
              case 'points':
                self.setRequestsPointsCount(count);
                self.pointRequests &&
                  self.setPointRequests(self.updateDataAfterManageRequest(self.pointRequests, status, id));
                break;
              case 'products':
                self.setRequestsProductsCount(count);
                self.productRequests &&
                  self.setProductRequests(self.updateDataAfterManageRequest(self.productRequests, status, id));
                break;
              case 'catalogues':
                self.setRequestsCataloguesCount(count);
                self.catalogueRequests &&
                  self.setCatalogueRequests(self.updateDataAfterManageRequest(self.catalogueRequests, status, id));
                break;
            }
          }
        )
        .catch(self.errorHandling);
    }),
  }));

export type IRequestsStore = Instance<typeof RequestsStore>;
